import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import fr from './fr';
import en from './en';

const RESOURCES = { fr, en };
const STORAGE_KEY = 'izm.locale';
const DEFAULT_LOCALE = 'fr';
const SUPPORTED_LOCALES = ['fr', 'en'];

const LocaleContext = createContext(null);

function getByPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

function interpolate(template, params) {
  if (!params) return template;
  return String(template).replace(/\{(\w+)\}/g, (_, k) => (params[k] !== undefined ? String(params[k]) : `{${k}}`));
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
    } catch {
      // ignore
    }
    return DEFAULT_LOCALE;
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    } catch {
      // ignore
    }
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (SUPPORTED_LOCALES.includes(next)) setLocaleState(next);
  }, []);

  const toggleLocale = useCallback(() => {
    setLocaleState((prev) => (prev === 'fr' ? 'en' : 'fr'));
  }, []);

  const t = useCallback(
    (key, params) => {
      const dict = RESOURCES[locale] || RESOURCES[DEFAULT_LOCALE];
      const value = getByPath(dict, key);
      if (value === undefined) {
        const fallback = getByPath(RESOURCES[DEFAULT_LOCALE], key);
        if (fallback === undefined) return key;
        return interpolate(fallback, params);
      }
      if (typeof value !== 'string') return key;
      return interpolate(value, params);
    },
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      toggleLocale,
      availableLocales: SUPPORTED_LOCALES,
      t,
    }),
    [locale, setLocale, toggleLocale, t]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

export function useT() {
  return useLocale().t;
}
